// frontend/src/admin/NotificationTester.jsx
import { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Box, Stack, Typography, Select, MenuItem, TextField, Button, Chip,
  FormControlLabel, Checkbox, Alert, Divider, Card, CardContent,
} from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import RefreshButton from '../components/common/RefreshButton'
import DisabledReason from '../components/common/DisabledReason'
import { FS, SP, PANEL_SX } from '../theme/tokens'
import { formatBerlinTime } from '../utils/time'
import { toast } from 'sonner'

const API = process.env.REACT_APP_API_BASE_URL
const ALL_CHANNELS = ['in_app', 'push', 'email', 'digest_morning', 'digest_evening', 'digest_weekly']

function defaultPayloadForTrigger(triggerKey) {
  if (triggerKey.startsWith('rma.')) return { case_id: 1 }
  if (triggerKey.startsWith('po.')) return { po_id: 'PO-TEST-1' }
  if (triggerKey.startsWith('pricing.')) return { sku: 'TEST-SKU', market_code: 'DE' }
  if (triggerKey.startsWith('sync.')) return { failed_scripts: ['sync_products.py'] }
  if (triggerKey.startsWith('ops.')) return { job_id: 1 }
  return {}
}

const CHANNEL_DOCS = {
  in_app: 'Bell badge + dropdown + sonner toast (live via SSE).',
  push: 'Native browser/iOS push notification (requires VAPID + permission grant + iOS install for Safari).',
  email: 'Flask-Mail via configured SMTP. Logged in /admin/email-log.',
  digest_morning: 'Aggregated into 7am cron digest with Haiku-synthesized prose.',
  digest_evening: 'Aggregated into 7pm cron digest with structured table.',
  digest_weekly: 'Aggregated into Monday 8am weekly digest.',
}

export default function NotificationTester() {
  const [triggers, setTriggers] = useState([])
  const [selectedKey, setSelectedKey] = useState('')
  const [payload, setPayload] = useState('{}')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [deepLink, setDeepLink] = useState('')
  const [overrideChannels, setOverrideChannels] = useState({})
  const [useOverride, setUseOverride] = useState(false)
  const [result, setResult] = useState(null)
  const [recent, setRecent] = useState([])
  const [firing, setFiring] = useState(false)

  useEffect(() => {
    axios.get(`${API}/api/admin/notify/triggers`)
      .then(r => setTriggers(r.data.triggers || []))
      .catch(() => setTriggers([]))
    refreshRecent()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // API is a module-level constant — omitting it is intentional

  const refreshRecent = () => {
    axios.get(`${API}/api/admin/notify/test-fires/recent?limit=10`)
      .then(r => setRecent(r.data.notifications || []))
      .catch(() => setRecent([]))
  }

  const onTriggerChange = (key) => {
    setSelectedKey(key)
    const t = triggers.find(x => x.trigger_key === key)
    if (t) {
      setPayload(JSON.stringify(defaultPayloadForTrigger(key), null, 2))
      setTitle(`Test fire — ${t.label}`)
      setBody(t.description || '')
      setOverrideChannels(Object.fromEntries(
        ALL_CHANNELS.map(c => [c, (t.default_channels || []).includes(c)])
      ))
    }
  }

  const fire = async () => {
    if (!selectedKey) return
    setFiring(true)
    setResult(null)
    let parsedPayload
    try {
      parsedPayload = JSON.parse(payload || '{}')
    } catch (e) {
      toast.error('Invalid JSON payload')
      setFiring(false)
      return
    }
    const reqBody = {
      trigger_key: selectedKey,
      payload: parsedPayload,
      title,
      body,
      deep_link: deepLink || undefined,
    }
    if (useOverride) {
      reqBody.override_channels = ALL_CHANNELS.filter(c => overrideChannels[c])
    }
    try {
      const res = await axios.post(`${API}/api/admin/notify/test-fire`, reqBody)
      setResult(res.data)
      toast.success(`Notification ${res.data.notification_id} fired`)
      refreshRecent()
    } catch (e) {
      const msg = e.response?.data?.error || 'Fire failed'
      toast.error(msg)
      setResult({ error: msg })
    } finally {
      setFiring(false)
    }
  }

  const reFire = (notif) => {
    setSelectedKey(notif.trigger_key)
    const t = triggers.find(x => x.trigger_key === notif.trigger_key)
    setPayload(JSON.stringify(defaultPayloadForTrigger(notif.trigger_key), null, 2))
    setTitle(notif.title)
    setBody(notif.body || '')
    if (t) {
      setOverrideChannels(Object.fromEntries(
        ALL_CHANNELS.map(c => [c, (t.default_channels || []).includes(c)])
      ))
    }
  }

  const selectedTrigger = triggers.find(t => t.trigger_key === selectedKey)

  // Kopf und Einzug kommen von `AdminLayout` (Abschnittsname als `h1`,
  // Brotkrume „Admin › …", Tab-Leiste, `PageContainer`) — die Seite rendert
  // nur ihren Inhalt.
  return (
    <>

      <Alert severity="info" sx={{ mb: SP.section, fontSize: FS.sm }}>
        Fire any registered trigger through the real dispatcher to verify in-app bell, sonner toast, push (if subscribed), and email channels. Test notifications are marked with <code>_test_fire: true</code> in payload so they can be filtered later. Dedup is disabled — fire as many times as you want.
      </Alert>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={SP.section}>
        {/* Left: Tester form */}
        <Box sx={{ flex: 2, ...PANEL_SX, p: SP.section }}>
          <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.sm }}>1. Pick a trigger</Typography>
          <Select
            size="small" fullWidth value={selectedKey}
            onChange={e => onTriggerChange(e.target.value)} displayEmpty
            sx={{ mb: SP.section }}
          >
            <MenuItem value="" disabled>— Select a trigger —</MenuItem>
            {triggers.map(t => (
              <MenuItem key={t.trigger_key} value={t.trigger_key}>
                {t.trigger_key} ({t.domain})
              </MenuItem>
            ))}
          </Select>

          {selectedTrigger && (
            <Box sx={{ mb: SP.section, p: SP.sm, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography sx={{ fontSize: FS.sm, fontWeight: 600 }}>{selectedTrigger.label}</Typography>
              <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: 0.5 }}>
                {selectedTrigger.description}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`intent: ${selectedTrigger.default_intent}`} />
                <Chip size="small" label={`urgency: ${selectedTrigger.default_urgency}`} color={selectedTrigger.default_urgency === 'sla_breach' ? 'error' : selectedTrigger.default_urgency === 'high' ? 'warning' : 'default'} />
                <Chip size="small" label={`enabled: ${selectedTrigger.enabled}`} color={selectedTrigger.enabled ? 'success' : 'default'} />
                {(selectedTrigger.default_channels || []).map(c => (
                  <Chip key={c} size="small" label={c} variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}

          <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.sm }}>2. Payload (JSON)</Typography>
          <TextField
            fullWidth multiline rows={5} size="small"
            value={payload} onChange={e => setPayload(e.target.value)}
            sx={{ mb: SP.section, fontFamily: 'monospace' }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: SP.section }}>
            <TextField fullWidth size="small" label="Title" value={title} onChange={e => setTitle(e.target.value)} />
            <TextField fullWidth size="small" label="Deep link (optional)" value={deepLink} onChange={e => setDeepLink(e.target.value)} placeholder="/rma/42" />
          </Stack>
          <TextField
            fullWidth multiline rows={2} size="small" label="Body" value={body}
            onChange={e => setBody(e.target.value)} sx={{ mb: SP.section }}
          />

          <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.sm }}>3. Channel override (optional)</Typography>
          <FormControlLabel
            control={<Checkbox size="small" checked={useOverride} onChange={e => setUseOverride(e.target.checked)} />}
            label={<Typography sx={{ fontSize: FS.xs }}>Override default channels</Typography>}
          />
          {useOverride && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: SP.section }}>
              {ALL_CHANNELS.map(c => (
                <FormControlLabel
                  key={c}
                  control={<Checkbox size="small" checked={!!overrideChannels[c]} onChange={e => setOverrideChannels({ ...overrideChannels, [c]: e.target.checked })} />}
                  label={<Typography sx={{ fontSize: FS.xs }}>{c}</Typography>}
                />
              ))}
            </Stack>
          )}

          <DisabledReason
            reason={firing
              ? null
              : !selectedKey ? 'Select a notification type first.' : null}
          >
            <Button
              variant="contained" startIcon={<PlayArrowIcon />}
              onClick={fire} disabled={!selectedKey || firing} size="large"
            >
              {firing ? 'Firing…' : 'Fire test notification'}
            </Button>
          </DisabledReason>

          {result && (
            <Box sx={{ mt: SP.section, p: SP.sm, bgcolor: result.error ? 'error.light' : 'success.light', borderRadius: 1 }}>
              <Typography sx={{ fontSize: FS.sm, fontWeight: 600 }}>
                {result.error ? `Error: ${result.error}` : `Fired — notification #${result.notification_id}`}
              </Typography>
              {result.notification && (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ fontSize: FS.xs }}>
                    Status: <Chip size="small" label={result.notification.status} />
                  </Typography>
                  <Typography sx={{ fontSize: FS.xs, mt: 0.5 }}>
                    Channels attempted: {(result.notification.channels_attempted || []).join(', ') || '(none)'}
                  </Typography>
                  <Typography sx={{ fontSize: FS.xs }}>
                    Channels delivered: {(result.notification.channels_delivered || []).join(', ') || '(none)'}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Right: Recent + docs */}
        <Box sx={{ flex: 1 }}>
          <Card sx={{ ...PANEL_SX, mb: SP.section }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: SP.sm }}>
                <Typography sx={{ fontSize: FS.md, fontWeight: 600 }}>Recent test fires</Typography>
                {/* E8 (2026-10-07): derselbe Auffrisch-Baustein wie ueberall. */}
                <RefreshButton ariaLabel="Refresh recent test fires" onClick={refreshRecent} />
              </Stack>
              {recent.length === 0 ? (
                <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>No test fires yet.</Typography>
              ) : (
                recent.map(n => (
                  <Box key={n.id} sx={{ py: 1, borderBottom: '1px solid', borderBottomColor: 'divider' }}>
                    <Typography sx={{ fontSize: FS.xs, fontWeight: 600 }}>{n.title}</Typography>
                    <Typography sx={{ fontSize: FS.xxs, color: 'text.secondary' }}>
                      {n.trigger_key} · {formatBerlinTime(n.created_at)}
                    </Typography>
                    <Button size="small" sx={{ fontSize: FS.xxs, mt: 0.5 }} onClick={() => reFire(n)}>
                      Re-fire
                    </Button>
                  </Box>
                ))
              )}
            </CardContent>
          </Card>

          <Card sx={PANEL_SX}>
            <CardContent>
              <Typography sx={{ fontSize: FS.md, fontWeight: 600, mb: SP.sm }}>Channel reference</Typography>
              {Object.entries(CHANNEL_DOCS).map(([ch, doc]) => (
                <Box key={ch} sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: FS.xs, fontWeight: 600 }}>{ch}</Typography>
                  <Typography sx={{ fontSize: FS.xxs, color: 'text.secondary' }}>{doc}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 1.5 }} />
              <Typography sx={{ fontSize: FS.xs, fontWeight: 600 }}>Dispatcher pipeline</Typography>
              <Typography sx={{ fontSize: FS.xxs, color: 'text.secondary' }}>
                registry lookup → admin enable check → user-pref channel resolution → presence-aware suppression (same-case → no push) → quiet hours filter → dedup window check (30 min — disabled for test fires) → write notifications row → fan out to channels.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Stack>
    </>
  )
}
